import React, { useState } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Alert, Badge, Button, Card, Descriptions, message, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";
import { getErrorMessage } from "@fitness-app/utils";
import { getWorkoutHintSettings } from "@fitness-app/utils/src/programs/getWorkoutHintSettings";

import ModalForm from "~/components/ModalForm/ModalForm";
import TraineeWorkoutSettingsForm, {
  type TraineeWorkoutSettingsFormModel,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeSettings/TraineeWorkoutSettingsForm";
import { useTraineeProfile, useTraineeSettings } from "~/modules/Trainee/TraineeProfile/scope";
import { useUpdateTraineeSettings } from "./useUpdateTraineeSettings";

const TraineeWorkoutSettings = () => {
  const { t } = useTranslation(["workouts", "trainees"]);
  const { data: traineeSettings, isLoading: settingsLoading } = useTraineeSettings();
  const { data: traineeData } = useTraineeProfile();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const updateSettings = useUpdateTraineeSettings("workoutSettings");

  if (traineeData?.status !== TraineeStatus.ACTIVE) {
    return (
      <div style={{ padding: 20 }}>
        <Alert
          message={t("shouldHasActiveStatus", { ns: "trainees" })}
          description={t("featureNotAvailable", { ns: "trainees" })}
          showIcon
          type="warning"
        />
      </div>
    );
  }

  const hintSettings = getWorkoutHintSettings(traineeSettings?.workoutSettings);

  const handleSubmit = async (formData: TraineeWorkoutSettingsFormModel) => {
    try {
      // A failed save must keep the modal open - otherwise the trainer walks away believing
      // the change was persisted.
      await updateSettings.mutateAsync({ ...traineeSettings?.workoutSettings, ...formData });
      setIsFormVisible(false);
    } catch (e) {
      void message.error(getErrorMessage(e));
    }
  };

  return (
    <Card
      bordered={false}
      loading={settingsLoading}
      className="!shadow-none"
      extra={
        <Button size="small" icon={<EditOutlined />} type="primary" onClick={() => setIsFormVisible(true)}>
          {t("notifications.editSettings", { ns: "trainees" })}
        </Button>
      }
    >
      <Descriptions title={t("hint.settingsTitle")} bordered column={1} labelStyle={{ width: 350 }}>
        <Descriptions.Item label={t("hint.exercisesHints")}>
          {t(`hint.${hintSettings.weightHint.toLowerCase()}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t("hint.applyToRepeats")}>
          <Badge
            text={
              hintSettings.applyHintToRepeats
                ? t("notifications.active", { ns: "trainees" })
                : t("notifications.inactive", { ns: "trainees" })
            }
            status={hintSettings.applyHintToRepeats ? "processing" : "warning"}
          />
        </Descriptions.Item>
      </Descriptions>

      <div className="mt-6 pl-2.5">
        <Typography.Text type="secondary">
          {hintSettings.applyHintToRepeats ? t("hint.trainerWarning") : t("hint.trainerInfo")}
        </Typography.Text>
      </div>

      <ModalForm
        visible={isFormVisible}
        loading={updateSettings.isLoading}
        onCancel={() => setIsFormVisible(false)}
        title={t("hint.settingsTitle")}
      >
        <TraineeWorkoutSettingsForm
          onSubmit={handleSubmit}
          model={{
            weightHint: hintSettings.weightHint,
            applyHintToRepeats: hintSettings.applyHintToRepeats,
          }}
        />
      </ModalForm>
    </Card>
  );
};

export default TraineeWorkoutSettings;
